import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { fetchOrganizationUsers, updateUser } from '@account_module/services';
import { ORGANIZATION_QUERY_KEY } from '@account_module/constants/queryKeys';
import { useUser } from '@auth_module/hooks/useUser';
import { USER_ROLE_KEY } from '@account_module/constants/userKeys';

interface IMutateUser {
  id: string | undefined;
  userId: string | null;
  payload: { [x: string]: string };
}

export const useOrganizationUsers = (id: string | undefined) => {
  const queryClient = useQueryClient();
  const { user } = useUser();

  const {
    data,
    isLoading,
    isFetching: isFetchingUsers,
  } = useQuery({
    queryKey: [ORGANIZATION_QUERY_KEY.users, id],
    queryFn: () => fetchOrganizationUsers(id),
    enabled: !!id,
  });

  const currentUser = useMemo(() => {
    const _user = data?.find((u) => u.id === user?.id);

    return { ..._user, isAdmin: _user?.role === USER_ROLE_KEY.admin };
  }, [user, data]);

  const { mutateAsync: mutateUser, isLoading: isMutateUserLoading } = useMutation({
    mutationFn: async ({ id, userId, payload }: IMutateUser) =>
      await updateUser(id, userId, payload),
    onSuccess: () => queryClient.invalidateQueries([ORGANIZATION_QUERY_KEY.users]),
  });

  return {
    data,
    isLoading: !id ? false : isLoading,
    isFetchingUsers,
    isMutateUserLoading,
    currentUser,
    mutateUser,
  };
};
